import React, { useContext, useEffect } from "react";
import { Form, Field, useForm } from "react-final-form";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IntlContext } from "context";
import { useForgotPassword } from "hooks/authentication";
import { useForgotPasswordDialog } from "context/ForgotPasswordDialog";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { ROUTES } from "routes";

type ForgotPasswordFormValues = {
    username: string;
};

type ForgotPasswordFormProps = {
    open: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const FormResetter: React.FC<{ open: boolean; onReset: () => void }> = ({ open, onReset }) => {
    const form = useForm<ForgotPasswordFormValues>();

    useEffect(() => {
        if (!open) {
            form.reset();
            onReset();
        }
    }, [open, form, onReset]);

    return null;
};

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ open }) => {
    const intl = useContext(IntlContext);
    const forgotPasswordMutation = useForgotPassword();
    const { actions: { closeForgotPassword } } = useForgotPasswordDialog();
    const [displayForm, setDisplayForm] = React.useState(true);
    const [displayError, setDisplayError] = React.useState(false);

    const resetState = React.useCallback(() => {
        setDisplayForm(true);
        setDisplayError(false);
    }, []);

    const onSubmit = async (values: ForgotPasswordFormValues) => {
        try {
            await forgotPasswordMutation.mutateAsync(values.username);
            setDisplayForm(false);
        } catch (err: unknown) {
            if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                const responseData = err.response.data;
                if (!isRecord(responseData) || !("data" in responseData) || !isRecord(responseData.data)) {
                    throw err;
                }

                if (
                    "username" in responseData.data &&
                    isRecord(responseData.data.username) &&
                    responseData.data.username.message === intl.formatMessage("validation.exists")
                ) {
                    setDisplayError(true);
                    return;
                }
                return formatErrorResponse(responseData as { data: unknown });
            }
            throw err;
        }
    };

    if (!displayForm) {
        return (
            <p className="font-nunito text-base font-medium leading-6 text-indigo-600">
                {intl.formatMessage("authentication.forgot_password.success")}
            </p>
        );
    }

    return (
        <Form<ForgotPasswordFormValues>
            onSubmit={onSubmit}
            render={({ handleSubmit, submitting, pristine }) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    <FormResetter open={open} onReset={resetState} />

                    <div className="flex flex-col gap-1.5">
                        <label
                            htmlFor="forgot-password-username"
                            className="ml-1 font-nunito text-xs font-black uppercase leading-4 tracking-wide text-gray-900"
                        >
                            {intl.formatMessage("authentication.forgot_password.label.username")}
                        </label>
                        <Field name="username">
                            {({ input, meta }) => (
                                <>
                                    <Input
                                        {...input}
                                        id="forgot-password-username"
                                        type="email"
                                        placeholder="votre@email.com"
                                        autoComplete="email"
                                        autoFocus
                                        required
                                        className="h-16 rounded-3xl border-0 bg-slate-50 px-8 font-nunito text-base font-bold text-gray-900 outline outline-1 outline-slate-100 placeholder:text-gray-900/50 focus-visible:outline-2 focus-visible:outline-indigo-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                                    />
                                    {meta.submitError && (
                                        <div aria-live="polite" className="ml-1 text-xs text-red-500">
                                            {meta.submitError}
                                        </div>
                                    )}
                                </>
                            )}
                        </Field>
                        {displayError && (
                            <span className="font-nunito text-sm text-red-500">
                                {intl.formatMessage("authentication.forgot_password.error.username.exists")}
                                <Link
                                    to={ROUTES.REGISTER}
                                    onClick={closeForgotPassword}
                                    className="font-bold text-indigo-600 underline"
                                >
                                    ici
                                </Link>
                            </span>
                        )}
                    </div>

                    <Button
                        type="submit"
                        disabled={submitting || pristine}
                        palette="indigo"
                        size="lg"
                        className="h-12 w-full font-nunito text-base shadow-indigo-glow"
                    >
                        {intl.formatMessage("authentication.forgot_password.action")}
                        <ArrowRight className="h-5 w-5" />
                    </Button>
                </form>
            )}
        />
    );
};

export default ForgotPasswordForm;
